import { Loading } from '@chiroup/components/Loading';
import { Maintenance } from '@chiroup/components/Maintenance';
import dayjs from 'dayjs';
import React, { lazy, Suspense, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { v4 } from 'uuid';
import { MeContext } from '../../contexts/me.context';
import WebsocketContext from '../../contexts/websocket.context';
import useMe from '../../hooks/useMe';
import LoadingPage from '../common/LoadingPage';
import EHRTermsOfUseAcceptance from '../EHRTermsOfUseAcceptance';
import BillingBanner from './BillingBanner';

const NoClinicSelected = lazy(() => import('../NoClinicSelected'));
const ClinicSelectedContainer = lazy(() => import('./ClinicSelectedContainer'));

type Props = {
  children: any;
};

const Container: React.FC<Props> = ({ children }) => {
  const sessionId = useMemo(() => v4(), []);
  const { pathname, state }: any = useLocation();
  const {
    me,
    updateMe,
    unselectClinic,
    selectClinic,
    selectLocation,
    hasAccess,
    hasEHR,
    hasVoiceToChartAccess,
    hasVoiceToChartSubscription,
    activeLocations,
    selectedLocationFull,
    hasRole,
    downForMaintenance,
    impersonatedFlags,
    toggleImpersonatedFlag,
    impersonatedRole,
    setRole,
    clearRole,
  } = useMe(state?.qsPlan);
  const navigate = useNavigate();
  // const [modalState, setModalState] = useState<OpenClosedStates>(
  //   OpenClosedStates.Closed
  // );

  useEffect(() => {
    if (me?.selectedClinic) {
      const pastDue = me?.selectedClinic?.subscription?.status === 'past_due';
      const active = me?.selectedClinic?.subscription?.status === 'active';
      const trialing = me?.selectedClinic?.subscription?.status === 'trialing';
      const pastDueDays = me?.selectedClinic?.subscription?.pastDueDays;
      const billDateHasNotPassed = dayjs(me?.selectedClinic?.billDate) > dayjs();
      const requiresAction =
        (me?.selectedClinic?.subscription?.status === 'requires_action' ||
          me?.selectedClinic?.subscription?.status === 'incomplete') &&
        billDateHasNotPassed;
      const pastDueUnder7Days = pastDue && typeof pastDueDays === 'number' && pastDueDays < 7;
      const blockEverything = !active && !trialing && !pastDueUnder7Days && !requiresAction;

      // console.log({
      //   pastDue,
      //   active,
      //   trialing,
      //   pastDueDays,
      //   billDateHasNotPassed,
      //   requiresAction,
      //   pastDueUnder7Days,
      //   blockEverything,
      // });
      if (blockEverything) {
        navigate('/settings/subscription');
      }
    }
  }, [me?.selectedClinic, navigate, pathname]);

  // TODO: FYI if we add this back, it is asking users who also have clinics that aren't education clinics to upload their student ID - it should only do this for education clinics
  // useEffect(() => {
  //   if (me) {
  //     if (me.studentRecord) {
  //       if (
  //         !me.studentRecord.studentId?.length &&
  //         dayjs().diff(dayjs(me?.createDate), 'hours') >= 48
  //       ) {
  //         if (pathname !== '/') {
  //           navigate('/');
  //         }
  //         setModalState(OpenClosedStates.Open);
  //       }
  //     }
  //   }
  // }, [history, me, pathname]);

  // const close = () => {
  //   getMe();
  //   setModalState(OpenClosedStates.Closed);
  // };

  return me ? (
    <Suspense fallback={<Loading />}>
      <MeContext.Provider
        value={{
          me,
          updateMe,
          unselectClinic,
          selectClinic,
          selectLocation,
          hasAccess,
          hasEHR,
          hasVoiceToChartAccess,
          hasVoiceToChartSubscription,
          activeLocations,
          selectedLocationFull,
          hasRole,
          sessionId,
          impersonatedFlags,
          toggleImpersonatedFlag,
          impersonatedRole,
          setRole,
          clearRole,
        }}
      >
        <WebsocketContext>
          {me.selectedClinic ? (
            <ClinicSelectedContainer>
              <BillingBanner />
              <EHRTermsOfUseAcceptance
                isOpen={me?.selectedClinic && hasEHR && !me?.hasAcceptedEHRTerms}
              />
              {children}
            </ClinicSelectedContainer>
          ) : (
            <NoClinicSelected clinics={me.clinics} select={selectClinic} />
          )}
        </WebsocketContext>
      </MeContext.Provider>
    </Suspense>
  ) : downForMaintenance ? (
    <Maintenance title={downForMaintenance.title} message={downForMaintenance.message} />
  ) : (
    <LoadingPage />
  );
};

export default Container;
